import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IonContent, IonPage, IonSpinner, IonText, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { getUserSelector } from '../../../user/selectors'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import HeaderNav from '../../../../components/headerNav/HeaderNav.component'
import { TabSegmentButtons } from '../../../../components/segmentButtons/tabSegmentButtons/TabSegmentButtons.component'
import { ISegmentButton } from '../../../../components/segmentButtons/SegmentButtons.component'
import { AppRouterConstants, qaFlagChartSummaryPath } from '../../../../components/appRouter/AppRouter.constants'
import { getQaAddendumSelector } from '../selectors'
import { getPcrIdSelector } from '../../../home/selectors'
import {
  fetchChartSummary,
  setAddendumText,
  setSelectedFlagId,
  submitQaFlagAddendum,
} from '../store/qaAddendum.actions'
import { IQaChartSummaryPage } from '../store/qaAddendum.types'
import { QaAddendumService } from '../services/qaAddendum.service'
import ChartSummaryPageListContent from '../content/QaAddendumChartSummary/ChartSummaryPageList.content'
import FlagResolutionDetailsContent from '../content/QaAddendumFlagResolution/FlagResolutionDetails.content'
import { toChartSummarySlug } from '../../utils/qaAddendumChartSummary.util'
import useDeviceMetrics from '../../../../utilities/useDeviceMetrics'
import { goBack } from '../../../../utilities/navigation.util'
import QaFlagActionFooter, { IQaFlagActionFooterProps } from '../../components/QaFlagActionFooter.component'
import QaFlagHeader from '../../components/QaFlagHeader.component'
import { useQAFlagNavigation } from '../../hooks/useQAFlagNavigation.hook'
import { useKeyboardOverlay } from '../../hooks/useKeyboardOverlay.hook'
import Logger from '../../../../services/logger.service'
import './FlagResolution.page.css'
import { saveRuleErrors } from '../../../rules/store/rules.actions'
import { getAllPCRDocs } from '../../../home/store/home.actions'
import { clearLocalNotifications } from '../../../../utilities/localNotifications.util'

const DETAILS_TAB = 1
const CHART_TAB = 2
const RESOLUTION_SEGMENTS: ISegmentButton[] = [
  { id: DETAILS_TAB, name: 'qaFlags.qaAddendum.flagResolution.detailsTab' },
  { id: CHART_TAB, name: 'qaFlags.qaAddendum.flagResolution.chartSummaryTab' },
]

const FlagResolutionPage: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const { flagId, flagDetail, isDetailLoading } = useQAFlagNavigation()
  const { isTablet } = useDeviceMetrics()
  const pcrId = useSelector(getPcrIdSelector)
  const { isChartSummaryLoading, chartSummaryFlagId, addendumText, isSubmitting } = useSelector(getQaAddendumSelector)
  const user = useSelector(getUserSelector)
  const { serviceId, userId } = user

  const [activeTabId, setActiveTabId] = useState(DETAILS_TAB)
  const [isExpanded, setIsExpanded] = useState(false)
  const fromChartSummaryDetailRef = useRef(false)
  useKeyboardOverlay()

  const resetFooterState = useCallback((resetTab = true) => {
    setIsExpanded(false)
    if (resetTab) setActiveTabId(DETAILS_TAB)
  }, [])

  useIonViewWillEnter(() => {
    const fromChartSummary = fromChartSummaryDetailRef.current
    fromChartSummaryDetailRef.current = false
    resetFooterState(!fromChartSummary)
  })

  useIonViewWillLeave(() => {
    setIsExpanded(false)
  })

  useEffect(() => {
    resetFooterState()
  }, [flagId])

  const onDetailsTab = activeTabId === DETAILS_TAB

  const handleExit = () => {
    QaAddendumService.cancelChartSummaryRequest()
    dispatch(setSelectedFlagId(null))
    if (pcrId) {
      Logger.info(`PCR: ${pcrId} closed by user: ${userId} in service: ${serviceId}`)
      dispatch(saveRuleErrors())
      dispatch(getAllPCRDocs())
      clearLocalNotifications()
      history.push(AppRouterConstants.HOME_ROUTE)
    } else goBack()
  }

  const handleTabChange = (segment: ISegmentButton) => {
    setActiveTabId(segment.id)
    setIsExpanded(false)
    if (segment.id === CHART_TAB && flagId && !isChartSummaryLoading && chartSummaryFlagId !== flagId) {
      dispatch(fetchChartSummary(flagId))
    }
  }

  const flowOrigin = (history.location.state as { from?: string } | undefined)?.from

  const handlePageClick = (page: IQaChartSummaryPage) => {
    fromChartSummaryDetailRef.current = true
    history.push(qaFlagChartSummaryPath(toChartSummarySlug(page.pageName)), { from: flowOrigin })
  }

  const blurActiveElement = useCallback(() => {
    const ele = document.activeElement as HTMLElement
    if (ele) ele?.blur()
  }, [])

  const handleReassignClick = useCallback(() => {
    blurActiveElement()
    history.push(AppRouterConstants.QA_ADDENDUM_FLAG_REASSIGN_ROUTE, { from: flowOrigin })
  }, [blurActiveElement, history, flowOrigin])

  const handleAssignmentHistoryClick = useCallback(() => {
    blurActiveElement()
    history.push(AppRouterConstants.QA_ADDENDUM_FLAG_HISTORY_ROUTE, { from: flowOrigin })
  }, [blurActiveElement, history, flowOrigin])

  const handleAddendumChange = (text: string) => dispatch(setAddendumText(text))

  const handleSubmitAddendum = () => dispatch(submitQaFlagAddendum())

  const resolutionSegments = RESOLUTION_SEGMENTS.map((s) => ({ ...s, name: t(s.name) }))
  const selectedTab = resolutionSegments.find((s) => s.id === activeTabId) ?? resolutionSegments[0]

  let footerProps: IQaFlagActionFooterProps | null = null
  if (flagDetail && !isDetailLoading && onDetailsTab) {
    footerProps = {
      textareaLabel: t('qaFlags.qaAddendum.flagResolution.resolveViaAddendum'),
      dataTestId: 'QaAddendumResolveTextArea',
      value: addendumText,
      onChange: handleAddendumChange,
      maxLength: 4000,
      isExpanded,
      onExpandedChange: setIsExpanded,
      primaryLabel: t('qaFlags.qaAddendum.flagResolution.submit'),
      canPrimary: addendumText.trim().length > 0 && !isSubmitting,
      isCancelDisabled: isSubmitting,
      onCancel: handleExit,
      onPrimary: handleSubmitAddendum,
      primaryTestId: 'QaAddendumSubmitButton',
    }
  }

  const renderContent = () => {
    if (!flagDetail)
      return (
        <IonText className='qa-addendum-flag-resolution-empty ion-padding' data-testid='QaFlagDetailEmpty'>
          {t('qaFlags.qaAddendum.flagResolution.noDetails')}
        </IonText>
      )

    return (
      <>
        {!onDetailsTab && (
          <ChartSummaryPageListContent isLoading={isChartSummaryLoading} onPageClick={handlePageClick} />
        )}
        {onDetailsTab && (
          <FlagResolutionDetailsContent
            isExpanded={isExpanded}
            onReassignClick={handleReassignClick}
            onAssignmentHistoryClick={handleAssignmentHistoryClick}
          />
        )}
      </>
    )
  }

  return (
    <IonPage id='QaAddendumFlagResolutionPage'>
      <QaFlagHeader showMenuButton={false} />
      <HeaderNav
        prevLabel={t('common.buttons.Back')}
        isBackButton
        title={t('qaFlags.qaAddendum.flagResolution.title')}
        prevOnClick={handleExit}
      />
      <IonContent
        fullscreen
        color={!onDetailsTab || isTablet ? 'light' : undefined}
        className='qa-addendum-flag-resolution-content'>
        <div className={`qa-addendum-shell${isExpanded ? ' qa-addendum-shell-expanded' : ''}`}>
          {isExpanded && (
            <div
              className='qa-addendum-flag-resolution-backdrop'
              onPointerDown={() => setIsExpanded(false)}
              aria-hidden='true'
            />
          )}
          <div
            className={`qa-addendum-flag-resolution-tabs${isTablet ? ' qa-addendum-flag-resolution-tabs-wide' : ''}`}>
            <TabSegmentButtons
              dataTestId='QaFlagResolutionTabs'
              className={isTablet ? '' : 'tabs-segment-full-width'}
              segmentsList={resolutionSegments}
              selected={selectedTab}
              onChangeAction={handleTabChange}
            />
          </div>
          <div
            className='qa-addendum-scroll'
            onClick={() => {
              if (isExpanded && onDetailsTab) {
                setIsExpanded(false)
              }
            }}>
            {isDetailLoading ? (
              <IonSpinner className='qa-addendum-loader' data-testid='QaFlagDetailSpinner' />
            ) : (
              renderContent()
            )}
          </div>
          {footerProps && <QaFlagActionFooter {...footerProps} />}
        </div>
      </IonContent>
    </IonPage>
  )
}

export default FlagResolutionPage
